<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-bold">Dog Colours</h2>
        <button
          @click="openForm()"
          type="button"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          ➕ New Colour
        </button>
      </div>

      <div v-if="masterDataStore.loading" class="text-center py-12">
        <div class="inline-block spinner w-8 h-8"></div>
      </div>
      <div v-else-if="masterDataStore.colours.length === 0" class="text-center py-12 text-gray-500">
        No colours found
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b">
            <tr>
              <th class="px-6 py-3 text-left text-sm font-semibold">ID</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Color</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Name</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Status</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="(colour, index) in masterDataStore.colours" :key="colour.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 text-sm text-gray-500">{{ colour.id }}</td>
              <td class="px-6 py-4 text-sm">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div :style="{ width: '24px', height: '24px', borderRadius: '4px', backgroundColor: colour.hex_code, border: '1px solid #ccc' }"></div>
                  <span class="text-xs text-gray-500">{{ colour.hex_code }}</span>
                </div>
              </td>
              <td class="px-6 py-4 text-sm font-medium">{{ colour.name }}</td>
              <td class="px-6 py-4 text-sm">
                <span :class="[
                  'px-2 py-1 rounded text-xs',
                  String(colour.is_active) === '1' || colour.is_active === true ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]">
                  {{ String(colour.is_active) === '1' || colour.is_active === true ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm space-x-2">
                <button @click="editColour(colour)" class="text-blue-600 hover:text-blue-800">Edit</button>
                <button @click="deleteColourFunc(colour.id)" class="text-red-600 hover:text-red-800">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Form Modal -->
    <div v-if="showForm" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 50;">
      <div style="background: white; padding: 24px; border-radius: 8px; width: 384px; position: relative; z-index: 51;">
        <h3 style="font-size: 18px; font-weight: bold; margin-bottom: 16px;">{{ editingColour ? 'Edit Colour' : 'New Colour' }}</h3>
        <form @submit.prevent="submitForm" style="display: flex; flex-direction: column; gap: 16px;">
          <div>
            <label style="display: block; font-size: 14px; font-weight: 500; margin-bottom: 4px;">Name *</label>
            <input v-model="formData.name" placeholder="e.g., Black & Brown" required style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" />
          </div>
          <div>
            <label style="display: block; font-size: 14px; font-weight: 500; margin-bottom: 4px;">Hex Code *</label>
            <div style="display: flex; gap: 8px; align-items: center;">
              <input type="color" v-model="formData.hex_code" required style="width: 60px; height: 40px; border: 1px solid #ccc; border-radius: 4px; cursor: pointer;" />
              <input v-model="formData.hex_code" placeholder="#FF5733" required pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$" style="flex: 1; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" />
            </div>
          </div>
          <div style="display: flex; align-items: center;">
            <input v-model="formData.is_active" type="checkbox" id="active-check" />
            <label for="active-check" style="margin-left: 8px; font-size: 14px;">Active</label>
          </div>
          <div style="display: flex; gap: 12px; padding-top: 16px; border-top: 1px solid #ccc;">
            <button type="button" @click="closeForm" style="flex: 1; padding: 8px; border: 1px solid #ccc; border-radius: 4px; cursor: pointer;">Cancel</button>
            <button type="submit" style="flex: 1; padding: 8px; background: #2563eb; color: white; border: none; border-radius: 4px; cursor: pointer;">{{ editingColour ? 'Update' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { useMasterDataStore } from '@/stores/masterDataStore'

const masterDataStore = useMasterDataStore()
const showForm = ref(false)
const editingColour = ref(null)
const formData = ref({ name: '', hex_code: '#000000', is_active: true })

const submitForm = async () => {
  try {
    if (!formData.value.name) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Name is required', confirmButtonColor: '#3085d6' })
      return
    }
    if (!formData.value.hex_code) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Hex code is required', confirmButtonColor: '#3085d6' })
      return
    }
    const data = {
      name: formData.value.name,
      hex_code: formData.value.hex_code.toUpperCase(),
      is_active: formData.value.is_active ? 1 : 0
    }
    if (editingColour.value) {
      await masterDataStore.updateColour(editingColour.value.id, data)
      Swal.fire({ icon: 'success', title: 'Success', text: 'Colour updated successfully', confirmButtonColor: '#3085d6' })
    } else {
      await masterDataStore.createColour(data)
      Swal.fire({ icon: 'success', title: 'Success', text: 'Colour created successfully', confirmButtonColor: '#3085d6' })
    }
    closeForm()
    await masterDataStore.getColours()
  } catch (error) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'Failed to save', confirmButtonColor: '#3085d6' })
  }
}

const closeForm = () => {
  showForm.value = false
  editingColour.value = null
  formData.value = { name: '', hex_code: '#000000', is_active: true }
}

const openForm = () => {
  showForm.value = true
}

const editColour = (colour) => {
  editingColour.value = colour
  formData.value = { 
    name: colour.name, 
    hex_code: colour.hex_code || '#000000',
    is_active: String(colour.is_active) === '1' || colour.is_active === true 
  }
  showForm.value = true
}

const deleteColourFunc = async (id) => {
  Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this colour?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      await masterDataStore.deleteColour(id)
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Colour deleted successfully', confirmButtonColor: '#3085d6' })
    }
  })
}

onMounted(async () => {
  await masterDataStore.getColours()
})
</script>
